/**
 * Public API functions for creating, fetching, updating, and executing
 * AI-managed objects via the ai-managed-object-service.
 */

import { AiManagedObjectApiError, executeGraphql } from './graphql-client.ts';
import type {
	AiManagedObject,
	AiManagedObjectCreateInput,
	AiManagedObjectExecutionResult,
	AiManagedObjectUpdateInput,
} from './types.ts';

// ---------------------------------------------------------------------------
// GraphQL fragments, queries, and mutations
// ---------------------------------------------------------------------------

const AI_MANAGED_OBJECT_FIELDS: string = `
  fragment AiManagedObjectFields on AiManagedObject {
    id
    lastModifiedBy
    lastExecutedAt
    lastExecutionStatus
    referenceObjectAri
    referenceProduct
    status
    type
    config {
      desiredUpdateFrequency {
        timezone
        type
        value
      }
      goal
      goalOperations
      prompt
      sources {
        app
        url
      }
      version
    }
  }
`;

const CREATE_AI_MANAGED_OBJECT: string = `
  mutation CreateAiManagedObject($cloudId: ID!, $input: AiManagedObjectCreateInput!) {
    aiManagedObject_createAiManagedObject(cloudId: $cloudId, input: $input) {
      ...AiManagedObjectFields
    }
  }
  ${AI_MANAGED_OBJECT_FIELDS}
`;

const UPDATE_AI_MANAGED_OBJECT: string = `
  mutation UpdateAiManagedObject($id: ID!, $input: AiManagedObjectUpdateInput!) {
    aiManagedObject_updateAiManagedObject(id: $id, input: $input) {
      ...AiManagedObjectFields
    }
  }
  ${AI_MANAGED_OBJECT_FIELDS}
`;

const GET_AI_MANAGED_OBJECT: string = `
  query GetAiManagedObject($id: ID!) {
    aiManagedObject_aiManagedObject(id: $id) {
      ...AiManagedObjectFields
    }
  }
  ${AI_MANAGED_OBJECT_FIELDS}
`;

const EXECUTE_AI_MANAGED_OBJECT: string = `
  mutation ExecuteAiManagedObject($id: ID!) {
    aiManagedObject_execute(id: $id) {
      aiManagedObject {
        ...AiManagedObjectFields
      }
      output
    }
  }
  ${AI_MANAGED_OBJECT_FIELDS}
`;

const ARCHIVE_AI_MANAGED_OBJECT: string = `
  mutation ArchiveAiManagedObject($id: ID!) {
    aiManagedObject_archiveAiManagedObject(id: $id)
  }
`;

const REACTIVATE_AI_MANAGED_OBJECT: string = `
  mutation ReactivateAiManagedObject($id: ID!) {
    aiManagedObject_reactivateAiManagedObject(id: $id)
  }
`;

// ---------------------------------------------------------------------------
// Response shapes (internal to this module)
// AGG exposes the service fields at the top level using the naming convention
// `aiManagedObject_<fieldName>` (no nested namespace object).
// ---------------------------------------------------------------------------

interface CreateAiManagedObjectResponse {
	aiManagedObject_createAiManagedObject: AiManagedObject;
}

interface UpdateAiManagedObjectResponse {
	aiManagedObject_updateAiManagedObject: AiManagedObject;
}

interface GetAiManagedObjectResponse {
	aiManagedObject_aiManagedObject: AiManagedObject | null;
}

interface ExecuteAiManagedObjectResponse {
	aiManagedObject_execute: AiManagedObjectExecutionResult | null;
}

interface ArchiveAiManagedObjectResponse {
	aiManagedObject_archiveAiManagedObject: boolean | null;
}

interface ReactivateAiManagedObjectResponse {
	aiManagedObject_reactivateAiManagedObject: boolean | null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a new AI-managed object.
 *
 * The `cloudId` is passed as a top-level argument to the AGG mutation (separate
 * from the `input` object) as required by the schema.
 *
 * @returns The created {@link AiManagedObject} with its server-issued `id`.
 * @throws {AiManagedObjectApiError} on network or GraphQL errors.
 */
export async function createAiManagedObject(
	input: AiManagedObjectCreateInput,
): Promise<AiManagedObject> {
	const { cloudId, ...restInput } = input;
	const result = await executeGraphql<CreateAiManagedObjectResponse>(CREATE_AI_MANAGED_OBJECT, {
		cloudId,
		input: restInput,
	});

	if (result.errors?.length) {
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	const created = result.data?.aiManagedObject_createAiManagedObject;
	if (!created) {
		throw new AiManagedObjectApiError('No data returned from createAiManagedObject');
	}

	return created;
}

/**
 * Update the configuration of an existing AI-managed object.
 *
 * The `id` is passed as a top-level argument to the AGG mutation (separate
 * from the `input` object) as required by the schema.
 *
 * @returns The updated {@link AiManagedObject}.
 * @throws {AiManagedObjectApiError} on network or GraphQL errors.
 */
export async function updateAiManagedObject(
	input: AiManagedObjectUpdateInput,
): Promise<AiManagedObject> {
	const { id, ...restInput } = input;
	const result = await executeGraphql<UpdateAiManagedObjectResponse>(UPDATE_AI_MANAGED_OBJECT, {
		id,
		input: restInput,
	});

	if (result.errors?.length) {
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	const updated = result.data?.aiManagedObject_updateAiManagedObject;
	if (!updated) {
		throw new AiManagedObjectApiError('No data returned from updateAiManagedObject');
	}

	return updated;
}

/**
 * Fetch a single AI-managed object by its identifier.
 *
 * @returns The {@link AiManagedObject}, or `null` if not found.
 * @throws {AiManagedObjectApiError} on network or GraphQL errors (other than 404).
 */
export async function fetchAiManagedObject(id: string): Promise<AiManagedObject | null> {
	const result = await executeGraphql<GetAiManagedObjectResponse>(GET_AI_MANAGED_OBJECT, { id });

	if (result.errors?.length) {
		// Treat NOT_FOUND-style errors as null rather than throwing
		const isNotFound = result.errors.some(
			(e) =>
				e.extensions?.statusCode === 404 ||
				e.extensions?.classification === 'NOT_FOUND' ||
				e.message?.toLowerCase().includes('not found'),
		);
		if (isNotFound) {
			return null;
		}
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	return result.data?.aiManagedObject_aiManagedObject ?? null;
}

/**
 * Execute an AI-managed object and return generated output.
 *
 * @throws {AiManagedObjectApiError} on network, GraphQL, or empty-response errors.
 */
export async function executeAiManagedObject(id: string): Promise<AiManagedObjectExecutionResult> {
	const result = await executeGraphql<ExecuteAiManagedObjectResponse>(
		EXECUTE_AI_MANAGED_OBJECT,
		{
			id,
		},
		{ slow: true },
	);

	if (result.errors?.length) {
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	const executionResult = result.data?.aiManagedObject_execute;
	if (!executionResult) {
		throw new AiManagedObjectApiError('No data returned from executeAiManagedObject');
	}

	return executionResult;
}

/**
 * Archive an AI-managed object.
 *
 * @throws {AiManagedObjectApiError} on network, GraphQL, or unsuccessful-response errors.
 */
export async function archiveAiManagedObject(id: string): Promise<void> {
	const result = await executeGraphql<ArchiveAiManagedObjectResponse>(ARCHIVE_AI_MANAGED_OBJECT, {
		id,
	});

	if (result.errors?.length) {
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	const archived = result.data?.aiManagedObject_archiveAiManagedObject;
	if (archived == null) {
		throw new AiManagedObjectApiError('No data returned from archiveAiManagedObject');
	}

	if (!archived) {
		throw new AiManagedObjectApiError('archiveAiManagedObject returned false');
	}
}

/**
 * Reactivate an archived AI-managed object.
 *
 * @throws {AiManagedObjectApiError} on network, GraphQL, or unsuccessful-response errors.
 */
export async function reactivateAiManagedObject(id: string): Promise<void> {
	const result = await executeGraphql<ReactivateAiManagedObjectResponse>(
		REACTIVATE_AI_MANAGED_OBJECT,
		{ id },
	);

	if (result.errors?.length) {
		throw new AiManagedObjectApiError(result.errors.map((e) => e.message).join('; '));
	}

	const reactivated = result.data?.aiManagedObject_reactivateAiManagedObject;
	if (reactivated == null) {
		throw new AiManagedObjectApiError('No data returned from reactivateAiManagedObject');
	}

	if (!reactivated) {
		throw new AiManagedObjectApiError('reactivateAiManagedObject returned false');
	}
}
